'use client';

import type { ModalWalkthroughStep } from '@unifyapps/defs/types/page/walkthrough';
import { useContainerElementContext } from '@unifyapps/ui/providers/ContainerElementContext';
import type { CSSProperties } from 'react';
import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useResolvedColor } from '../../../../../components/DrawCanvas/hooks/useResolvedColor';
import { useBlockAppearanceStyles } from '../../../../hooks/useBlockAppearanceStyles';
import { useWalkthroughManagerStore } from '../../../../stores/WalkthroughManagerStore';
import { WalkthroughStepContent } from '../components/WalkthroughStepContent';
import { resolveOverlayOpacity } from '../utils/resolveOverlayOpacity';

/**
 * WalkthroughModalDisplay - Handles modal centering and rendering.
 * Modal steps don't need target element or highlight overlay - they're centered on screen.
 *
 * Runner Mode: All step settings are respected, including:
 * - showAfter / showAfterDelay / showAfterScroll (delays/scroll triggers)
 * - overlayColor / overlayOpacity / enableInteractionWithPage
 * - modalPlacement / modalStyles
 */
export interface WalkthroughModalDisplayProps {
  step: ModalWalkthroughStep;
  container?: HTMLDivElement | null;
}

export function WalkthroughModalDisplay({ step, container: containerOverride }: WalkthroughModalDisplayProps) {
  const {
    rootBlockId,
    showBackdrop,
    modalPlacement = 'center',
    modalStyles: _modalStyles = {},
    overlayColor,
    overlayOpacity,
    enableInteractionWithPage = true,
  } = step;
  const container = useContainerElementContext();
  const portalContainer = containerOverride ?? container;
  const store = useWalkthroughManagerStore();

  // Modal steps are fast - commit display index immediately when rendered
  useEffect(() => {
    const state = store.getState();
    if (state.driverState.activeIndex !== state.driverState.displayIndex) {
      state.driverActions.commitDisplayIndex();
    }
  }, [store]);

  // extract modal class/style from appearance schema
  const { className, style: resolvedModalStyle } = useBlockAppearanceStyles({
    appearanceStyles: _modalStyles,
  });

  // resolve overlay color and opacity
  const resolvedOverlayColor = useResolvedColor(overlayColor, 'rgb(0, 0, 0)');
  const resolvedOverlayOpacity = resolveOverlayOpacity(overlayOpacity ?? 'opacity-75');

  // handle positioning context (container-aware/absolute vs. fixed)
  const isMountedInContainer = portalContainer !== null;

  const modalStyle = useMemo<CSSProperties>(() => {
    const baseStyle: CSSProperties = {
      position: isMountedInContainer ? 'absolute' : 'fixed',
      zIndex: 10001,
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: showBackdrop ? undefined : '0px 4px 12px rgba(0, 0, 0, 0.15)',
      maxWidth: isMountedInContainer ? 'calc(100% - 32px)' : 'calc(100vw - 32px)',
      maxHeight: isMountedInContainer ? 'calc(100% - 32px)' : 'calc(100vh - 32px)',
      overflow: 'auto',
      ...resolvedModalStyle,
    };

    // Apply placement positioning
    switch (modalPlacement) {
      case 'center':
        baseStyle.top = '50%';
        baseStyle.left = '50%';
        baseStyle.transform = 'translate(-50%, -50%)';
        break;
      case 'top':
        baseStyle.top = '16px';
        baseStyle.left = '50%';
        baseStyle.transform = 'translateX(-50%)';
        break;
      case 'bottom':
        baseStyle.bottom = '16px';
        baseStyle.left = '50%';
        baseStyle.transform = 'translateX(-50%)';
        break;
      case 'left':
        baseStyle.top = '50%';
        baseStyle.left = '16px';
        baseStyle.transform = 'translateY(-50%)';
        break;
      case 'right':
        baseStyle.top = '50%';
        baseStyle.right = '16px';
        baseStyle.transform = 'translateY(-50%)';
        break;
      case 'top-left':
        baseStyle.top = '16px';
        baseStyle.left = '16px';
        baseStyle.transform = 'none';
        break;
      case 'top-right':
        baseStyle.top = '16px';
        baseStyle.right = '16px';
        baseStyle.transform = 'none';
        break;
      case 'bottom-left':
        baseStyle.bottom = '16px';
        baseStyle.left = '16px';
        baseStyle.transform = 'none';
        break;
      case 'bottom-right':
        baseStyle.bottom = '16px';
        baseStyle.right = '16px';
        baseStyle.transform = 'none';
        break;
      default:
        baseStyle.top = '50%';
        baseStyle.left = '50%';
        baseStyle.transform = 'translate(-50%, -50%)';
    }

    return baseStyle;
  }, [showBackdrop, modalPlacement, resolvedModalStyle, isMountedInContainer]);

  const backdropStyle = useMemo<CSSProperties>(() => {
    return {
      position: isMountedInContainer ? 'absolute' : 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: showBackdrop ? resolvedOverlayColor : 'transparent',
      opacity: showBackdrop ? resolvedOverlayOpacity : 0,
      zIndex: 10000,
      pointerEvents: enableInteractionWithPage ? 'none' : 'auto',
    };
  }, [
    showBackdrop,
    resolvedOverlayColor,
    resolvedOverlayOpacity,
    enableInteractionWithPage,
    isMountedInContainer,
  ]);

  const modalContent = (
    <>
      <div data-walkthrough-backdrop style={backdropStyle} />
      <div className={className} data-walkthrough-modal style={modalStyle}>
        <WalkthroughStepContent rootBlockId={rootBlockId} />
      </div>
    </>
  );

  if (portalContainer) {
    return createPortal(modalContent, portalContainer);
  }

  return modalContent;
}
